/**
 * AlsoBadge — P3-10.2.
 *
 * Tiny pill-badges that surface the OTHER roles a legacy record plays
 * in the Master Counterparty Registry.
 *
 *   <AlsoBadge module="suppliers" recordId={supplier.id} current="supplier" />
 *
 * Example output: "Also: Client · Partner"
 *
 * Lookups are cached in a module-level Map for the life of the page to
 * avoid N+1 network calls when rendering long lists.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache keyed by `${module}:${record_id}`
const _cache = new Map();

const LABEL = {
  supplier: 'Supplier',
  client: 'Client',
  partner: 'Partner',
  customer: 'Customer',
  contact: 'Contact',
  lead: 'Lead',
  jv_partner: 'JV Partner',
};

const COLOR = {
  supplier: 'bg-blue-100 text-blue-700 border-blue-200',
  client: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  partner: 'bg-amber-100 text-amber-700 border-amber-200',
  customer: 'bg-violet-100 text-violet-700 border-violet-200',
  contact: 'bg-slate-100 text-slate-700 border-slate-200',
  lead: 'bg-red-100 text-red-700 border-red-200',
  jv_partner: 'bg-purple-100 text-purple-700 border-purple-200',
};

const AlsoBadge = ({ module, recordId, current }) => {
  const [types, setTypes] = useState([]);
  const [masterId, setMasterId] = useState(null);

  useEffect(() => {
    if (!recordId || !module) return;
    const cacheKey = `${module}:${recordId}`;
    if (_cache.has(cacheKey)) {
      const cached = _cache.get(cacheKey);
      setTypes(cached.types);
      setMasterId(cached.masterId);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const r = await axios.get(
          `${API_URL}/api/counterparties/by-legacy/${module}/${recordId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const payload = {
          types: r.data?.types || [],
          masterId: r.data?.counterparty_id || null,
        };
        _cache.set(cacheKey, payload);
        if (!cancelled) {
          setTypes(payload.types);
          setMasterId(payload.masterId);
        }
      } catch {
        /* silent — badge is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [module, recordId]);

  const others = (types || []).filter((t) => t !== current);
  if (!others.length || !masterId) return null;

  return (
    <a
      href={`/admin/counterparties/${masterId}`}
      className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700"
      title="Opens the Master Counterparty record"
      onClick={(e) => e.stopPropagation()}
      data-testid={`also-badge-${module}-${recordId}`}
    >
      <span className="uppercase tracking-wide font-semibold">Also:</span>
      {others.map((t) => (
        <span
          key={t}
          className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide ${COLOR[t] || COLOR.contact}`}
        >
          {LABEL[t] || t}
        </span>
      ))}
    </a>
  );
};

export default AlsoBadge;
